import { memo, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MdWarning } from 'react-icons/md';
import classnames from 'classnames/bind';

import { Divider, LabeledContainer, Checkbox, Input } from '@schibsted-svp/react-ui';
import useToggle from 'hooks/useToggle';
import Tooltip from 'components/ui/Tooltip';
import { RadioOptionRHF } from 'components/video/live/form/RadioOption';
import { TimeLengthSelect } from 'components/video/live/form/EncoderInputForm/TimeLengthSelect';
import { AccessDefinitionSelectRHF } from 'components/video/live/form/AccessDefinitionSelect';
import { AdvancedToggleButton } from 'components/video/live/form/AdvancedToggleButton';
import * as validatorHelpers from 'components/video/live/form/validators';
import type { EncoderInputFormData } from '../EncoderInputForm/types';
import type { MediaLiveInputFormData } from '../MediaLiveInputForm/types';
import { hasLogoOverlayOptions, LogoOverlaySelectRHF } from '../LogoOverlaySelect';
import { CuttingroomChannelSelectRHF } from '../CuttingroomChannelSelect';
import { useCuttingroomChannelsQuery } from '../../../../../services/admin-bff-sdk/generated';

import css from './VideoConfiguration.module.scss';

const cln = classnames.bind(css);

export interface VideoConfigurationFormData {
    streamQuality: 'standard' | 'normal';
    dvrLength?: number;
    access?: {
        geoblock: boolean;
        paywall?: string;
    };
    profile: {
        HLS1080: boolean;
    };
    livePreview?: boolean;
    liveArchive?: boolean;
    wscRtmpOutput?: boolean;
    audioOnly?: boolean;
    vertical?: boolean;
    useBackupEncoder: boolean;
    overlay?: string;
    htmlOverlay?: string;
    cuttingroomChannel?: string;
    deinterlace?: boolean;
    drmEncryption?: boolean;
}

interface VideoConfigurationProps {
    isEncrypted: boolean;
    enableLivePreview?: boolean;
    enableMediaLiveVertical?: boolean;
    enableLiveVertical?: boolean;
    enableDeinterlace?: boolean;
    enableLiveArchive?: boolean;
    enableWscRtmpOutput?: boolean;
    enableAudioOnlyLiveStream?: boolean;
    enableHtmlOverlay?: boolean;
    enableDrmEncryption?: boolean;
}

const videoSpecificFields = [
    'vertical',
    'livePreview',
    'deinterlace',
    'liveArchive',
    'wscRtmpOutput',
    'drmEncryption',
] as const;

export const VideoConfiguration = memo(
    ({
        isEncrypted,
        enableLivePreview = false,
        enableMediaLiveVertical = false,
        enableLiveVertical = false,
        enableDeinterlace = false,
        enableLiveArchive = false,
        enableWscRtmpOutput = false,
        enableAudioOnlyLiveStream = false,
        enableHtmlOverlay = false,
        enableDrmEncryption = false,
    }: VideoConfigurationProps) => {
        const { newsroom } = useParams();
        const { register, control, trigger, setValue } = useFormContext<
            EncoderInputFormData | MediaLiveInputFormData
        >();

        const [useBackupEncoder, streamQuality, source, inputType, audioOnly, is1080p] = useWatch({
            name: ['useBackupEncoder', 'streamQuality', 'source', 'inputType', 'audioOnly', 'profile.HLS1080'],
            control,
        });
        const disabledBurnInLogo = !source && !inputType;

        useEffect(() => {
            if (audioOnly) {
                [...videoSpecificFields, 'profile.HLS1080' as const].forEach((field) => setValue(field, false));
                setValue('overlay', '');
            }
        }, [audioOnly, setValue]);

        useEffect(() => {
            if (!is1080p) {
                setValue('liveArchive', false);
            }
        }, [is1080p, setValue]);

        const [isAdvancedExpanded, toggleIsAdvancedExpanded] = useToggle(false);
        const [isAdvancedFirstTimeExpanded, setIsAdvancedFirstTimeExpanded] = useState(false);

        useEffect(() => {
            trigger('dvrLength');
        }, [streamQuality, trigger]);

        const toggleAdvancedSection = () => {
            toggleIsAdvancedExpanded();
            setIsAdvancedFirstTimeExpanded(true);
        };

        const isStandardQualitySelected = streamQuality === 'standard';
        const isCustomQualitySelected = streamQuality === 'normal';
        const dvrLengthValidator = isCustomQualitySelected ? { required: validatorHelpers.required } : undefined;

        const cuttingroomChannelsResult = useCuttingroomChannelsQuery(
            { newsroom },
            { refetchOnMountOrArgChange: true }
        );

        const cuttingroomChannels = cuttingroomChannelsResult.data?.cuttingroomChannels || [];

        return (
            <div
                className={cln('advancedContainer', {
                    'advancedContainer--expanded': isAdvancedExpanded,
                })}
            >
                <Divider margin="10px 0" />

                <LabeledContainer
                    label="Configure live video"
                    className={cln('advanced', {
                        'advanced--expanded': isAdvancedExpanded,
                        'advanced--touched': isAdvancedFirstTimeExpanded,
                    })}
                >
                    <RadioOptionRHF
                        name="streamQuality"
                        value="standard"
                        label="Standard live video"
                        className={cln('qualityOption', {
                            'qualityOption--selected': isStandardQualitySelected,
                        })}
                    >
                        Normal latency and {isEncrypted ? '6' : '12'} hour DVR
                    </RadioOptionRHF>

                    <RadioOptionRHF
                        name="streamQuality"
                        value="normal"
                        label="Live video with custom DVR"
                        className={cln('qualityOption', {
                            'qualityOption--selected': isCustomQualitySelected,
                        })}
                    >
                        Normal latency
                    </RadioOptionRHF>

                    <LabeledContainer label="DVR Length" width={270} className={css.dvrLength}>
                        <TimeLengthSelect
                            name="dvrLength"
                            control={control}
                            setValue={setValue}
                            defaultLabel="No DVR"
                            disabled={!isCustomQualitySelected}
                            validate={dvrLengthValidator}
                        />
                    </LabeledContainer>

                    <LabeledContainer label="Viewer access" className={css.optionList}>
                        <Checkbox {...register('access.geoblock')} label="Geoblock" />
                        <AccessDefinitionSelectRHF
                            name="access.paywall"
                            disabled={useBackupEncoder}
                            size="compact"
                            className={css.paywallOption}
                        />
                    </LabeledContainer>

                    <LabeledContainer label="Live settings" className={css.optionList}>
                        {enableAudioOnlyLiveStream && (
                            <Tooltip
                                theme="dark"
                                placement="top"
                                content="Audio only stream will disable all video specific options"
                            >
                                <div>
                                    <Checkbox {...register('audioOnly')} label="Audio only stream" />
                                </div>
                            </Tooltip>
                        )}
                        <Checkbox {...register('profile.HLS1080')} label="1080p quality" disabled={audioOnly} />
                        {enableMediaLiveVertical && (
                            <Checkbox {...register('vertical')} label="Vertical input" disabled={audioOnly} />
                        )}
                        {enableLiveVertical && (
                            <Checkbox {...register('vertical')} label="Crop to vertical" disabled={audioOnly} />
                        )}
                        {enableLivePreview && (
                            <Checkbox {...register('livePreview')} label="Live preview" disabled={audioOnly} />
                        )}
                        {enableDeinterlace && (
                            <Checkbox {...register('deinterlace')} label="Deinterlace" disabled={audioOnly} />
                        )}
                        {enableLiveArchive && (
                            <Checkbox
                                {...register('liveArchive')}
                                label="Live archive"
                                disabled={audioOnly || !is1080p}
                            />
                        )}
                        {enableWscRtmpOutput && (
                            <Checkbox {...register('wscRtmpOutput')} label="Send to WSC Sports" disabled={audioOnly} />
                        )}
                        {enableDrmEncryption && (
                            <Checkbox {...register('drmEncryption')} label="DRM Encryption" disabled={audioOnly} />
                        )}
                    </LabeledContainer>

                    {hasLogoOverlayOptions(newsroom) ? (
                        <LabeledContainer
                            label="Burn-in logo"
                            width={270}
                            className={cln(css.optionList, css.burnInLogo)}
                        >
                            <LogoOverlaySelectRHF
                                name="overlay"
                                control={control}
                                newsroom={newsroom}
                                size="compact"
                                isDisabled={disabledBurnInLogo || audioOnly}
                            />
                            {disabledBurnInLogo ? (
                                <Tooltip theme="dark" placement="top" content="Choose source first">
                                    <div className={css.burnInLogoWarning}>
                                        <MdWarning />
                                    </div>
                                </Tooltip>
                            ) : null}
                        </LabeledContainer>
                    ) : null}

                    {cuttingroomChannels.length ? (
                        <LabeledContainer label="Cuttingroom channel" width={270} className={css.optionList}>
                            <CuttingroomChannelSelectRHF
                                name="cuttingroomChannel"
                                size="compact"
                                cuttingroomChannels={cuttingroomChannels}
                                className={css.cuttingroomChannelOption}
                                isDisabled={audioOnly}
                            />
                        </LabeledContainer>
                    ) : null}

                    {enableHtmlOverlay ? (
                        <LabeledContainer label="html5 overlay url" width={270} className={css.optionList}>
                            <Input className={css.htmlOverlayInput} {...register('htmlOverlay')} disabled={audioOnly} />
                        </LabeledContainer>
                    ) : null}
                </LabeledContainer>
                <AdvancedToggleButton onClick={toggleAdvancedSection} isExpanded={isAdvancedExpanded} />
            </div>
        );
    }
);

VideoConfiguration.displayName = 'VideoConfiguration';
